import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import s from './News.module.css';
import newsPhoto from '../../../assets/icons/newsPhoto.jpg';
import { ReactComponent as EditIcon } from '../../../assets/icons/PencilIcon.svg';
import { ReactComponent as DragIcon } from '../../../assets/icons/DragIcon.svg';
import { newsApi } from '../../../api/news';
import { News } from '../../../types/api.types';

export const AdminNews: React.FC = () => {
    const navigate = useNavigate();
    const [filterStatus, setFilterStatus] = useState<string>('published');
    const [news, setNews] = useState<News[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadNews();
    }, []);

    const loadNews = async () => {
        try {
            setLoading(true);
            const data = await newsApi.getAllAdmin();
            console.log('Новости с бэка:', data);
            setNews(data);
        } catch (error) {
            console.error('Ошибка загрузки новостей:', error);
        } finally {
            setLoading(false);
        }
    };

    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

    const filteredNews = filterStatus === 'all'
        ? news
        : news.filter(item => item.status === filterStatus);

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'published': return 'Опубликовано';
            case 'draft': return 'Черновик';
            case 'archived': return 'Архив';
            default: return status;
        }
    };

    const getStatusClass = (status: string) => {
        switch (status) {
            case 'published': return s.statusPublished;
            case 'draft': return s.statusDraft;
            case 'archived': return s.statusArchived;
            default: return '';
        }
    };

    const handleStatusChange = async (id: number, newStatus: string) => {
        try {
            await newsApi.update(id, { status: newStatus as 'published' | 'draft' | 'archived' });
            setNews(news.map(item =>
                item.id === id ? { ...item, status: newStatus as 'published' | 'draft' | 'archived' } : item
            ));
        } catch (error) {
            console.error('Ошибка обновления статуса:', error);
        }
    };

    const handleDelete = async (id: number) => {
        if (window.confirm('Удалить новость?')) {
            try {
                await newsApi.delete(id);
                setNews(news.filter(item => item.id !== id));
            } catch (error) {
                console.error('Ошибка удаления:', error);
            }
        }
    };

    const handleDragStart = (index: number) => {
        setDraggedIndex(index);
    };

    const handleDragOver = async (e: React.DragEvent, index: number) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === index) return;

        setDragOverIndex(index);

        const newNews = [...news];
        const [draggedItem] = newNews.splice(draggedIndex, 1);
        newNews.splice(index, 0, draggedItem);

        newNews.forEach((item, idx) => {
            item.sortOrder = idx + 1;
        });

        setNews(newNews);
        setDraggedIndex(index);

        try {
            const updatePromises = newNews.map(item =>
                newsApi.update(item.id, { sortOrder: item.sortOrder })
            );
            await Promise.all(updatePromises);
        } catch (error) {
            console.error('Ошибка сохранения порядка:', error);
            loadNews();
        }
    };

    const handleDragLeave = () => {
        setDragOverIndex(null);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const stripHtml = (html: string) => {
        if (!html) return '';
        const div = document.createElement('div');
        div.innerHTML = html;
        return div.textContent || div.innerText || '';
    };

    // 🔧 НОВАЯ ФУНКЦИЯ для извлечения картинки из HTML
    const extractFirstImageFromHtml = (html: string): string | null => {
        if (!html) return null;
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const img = doc.querySelector('img');
        return img ? img.getAttribute('src') : null;
    };

    if (loading) {
        return <div className={s.loading}>Загрузка...</div>;
    }

    return (
        <div className={s.news}>
            <div className={s.header}>
                <h1 className={s.title}>Новости</h1>
                <button
                    className={s.addBtn}
                    onClick={() => navigate('/admin/news/new')}
                >
                    + Добавить новость
                </button>
            </div>

            <div className={s.filterBar}>
                <button
                    className={`${s.filterBtn} ${filterStatus === 'published' ? s.filterActive : ''}`}
                    onClick={() => setFilterStatus('published')}
                >
                    Опубликовано
                </button>
                <button
                    className={`${s.filterBtn} ${filterStatus === 'draft' ? s.filterActive : ''}`}
                    onClick={() => setFilterStatus('draft')}
                >
                    Черновики
                </button>
                <button
                    className={`${s.filterBtn} ${filterStatus === 'archived' ? s.filterActive : ''}`}
                    onClick={() => setFilterStatus('archived')}
                >
                    Архив
                </button>
            </div>

            <div className={s.newsGrid}>
                {filteredNews.map((item, index) => {
                    const globalIndex = news.findIndex(n => n.id === item.id);

                    // 🔧 ИСПРАВЛЕННАЯ ЛОГИКА
                    const htmlContent = item.content?.html || '';
                    const firstImageSrc = extractFirstImageFromHtml(htmlContent);
                    const imageSrc = firstImageSrc || newsPhoto;

                    const displayText = item.displayDescription || item.title || '';

                    // Отладка
                    console.log(`📰 Новость "${item.title}":`, {
                        htmlContent: htmlContent.substring(0, 100),
                        firstImageSrc,
                        imageSrc
                    });

                    return (
                        <div
                            key={item.id}
                            className={`${s.newsCard} ${draggedIndex === globalIndex ? s.dragging : ''} ${dragOverIndex === globalIndex && draggedIndex !== globalIndex ? s.dragOver : ''}`}
                            draggable
                            onDragStart={() => handleDragStart(globalIndex)}
                            onDragOver={(e) => handleDragOver(e, globalIndex)}
                            onDragLeave={handleDragLeave}
                            onDragEnd={handleDragEnd}
                        >
                            <div className={s.cardHeader}>
                                <div className={s.cardLeft}>
                                    <div className={s.dragHandle}>
                                        <DragIcon className={s.dragIcon} />
                                    </div>
                                    <span className={s.orderNumber}>{item.sortOrder || index + 1}</span>
                                    <span className={`${s.statusBadge} ${getStatusClass(item.status)}`}>
                                        {getStatusLabel(item.status)}
                                    </span>
                                </div>
                                <button
                                    className={s.editBtn}
                                    onClick={() => navigate(`/admin/news/${item.id}`)}
                                >
                                    <EditIcon className={s.editIcon} />
                                </button>
                            </div>
                            <img
                                src={imageSrc}
                                alt={item.title}
                                className={s.newsImage}
                                onError={(e) => {
                                    console.log(`❌ Ошибка загрузки: ${imageSrc}`);
                                    e.currentTarget.src = newsPhoto;
                                }}
                            />
                            <div className={s.newsContent}>
                                <h3 className={s.newsTitle}>{item.title}</h3>
                                <p className={s.newsDescription}>
                                    {displayText.length > 100 ? displayText.substring(0, 100) + '...' : displayText}
                                </p>
                                <div className={s.newsDate}>{new Date(item.dateCreating).toLocaleDateString('ru-RU')}</div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};